import { useEffect, useRef, useState } from 'react';

import { fetchAccountTimeline } from '../api/account-timeline';

import type { AccountTimelineEntry } from '../api/account-timeline';

type State = {
  accounts: Record<string, AccountTimelineEntry>;
  isLoading: boolean;
  error: Error | null;
};

const INITIAL_STATE: State = {
  accounts: {},
  isLoading: false,
  error: null,
};

export function useAccountTimeline(accountIds: string[]): State {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const key = [...accountIds].sort().join(',');

  useEffect(() => {
    if (accountIds.length === 0) {
      setState(INITIAL_STATE);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ accounts: {}, isLoading: true, error: null });

    fetchAccountTimeline(accountIds)
      .then(data => {
        if (controller.signal.aborted) return;
        setState({ accounts: data.accounts, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          accounts: {},
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
